/**
 * Briefing — the daily-engagement landing surface.
 *
 * UI sprint step 3 reskin (2026-05-11): rebuilt against pwa-v2's
 * editorial briefing layout.
 *   - `.brief-masthead` — time-of-day greeting in Instrument Serif italic
 *     (44px) with the weather_line as the lede, stats bar on the right
 *   - `.brief-grid` — 2fr/1fr two-column responsive
 *   - Left card: Next actions due (the action-driving surface)
 *   - Right card: Going cold (accounts past their cadence)
 *
 * Triggers card is suppressed at MVP (Group D scanner not yet shipped).
 *
 * Renders the JSON artifact emitted by GET /api/briefing/today per
 * spec/briefing.md §4 / §7.
 */
import { useEffect, useState } from 'preact/hooks';
import {
  type BriefingArtifact,
  type MeResponse,
  addAccountOnboarding,
  getAccountCount,
  getBriefing,
  importCsv,
} from '../api.ts';
import { Icons } from '../components/icons';

interface BriefingProps {
  me: MeResponse | null;
  onSelectEntity: (id: string) => void;
  /** Bumps when a fresh capture lands so the briefing can re-fetch. */
  refreshKey: number;
}

export function Briefing({ me, onSelectEntity, refreshKey }: BriefingProps) {
  const [data, setData] = useState<BriefingArtifact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cacheState, setCacheState] = useState<'hit' | 'miss' | null>(null);
  // First-account empty-state (onboarding.md §6.1): when the tenant has zero
  // account entities, /today shows an empty-state card instead of the normal
  // briefing. `null` = not yet known; we don't render either branch until the
  // count resolves so the empty-state doesn't flash before real content.
  const [accountCount, setAccountCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getAccountCount()
      .then((n) => {
        if (!cancelled) setAccountCount(n);
      })
      .catch(() => {
        // Non-fatal: if the count fails, fall through to the normal briefing
        // render rather than blocking /today on it.
        if (!cancelled) setAccountCount(-1);
      });
    getBriefing()
      .then((r) => {
        if (cancelled) return;
        setData(r.briefing);
        setCacheState(r.cache);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // Zero-account tenants get the §6.1 empty-state in place of all normal
  // briefing content. `accountCount === -1` means the count call failed —
  // we fall through to the briefing rather than guessing.
  if (accountCount === 0) {
    return <FirstAccountEmptyState onAdded={() => setAccountCount((n) => (n ?? 0) + 1)} />;
  }

  if (error) {
    return (
      <div class="v2-page">
        <div class="error-banner">{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div class="v2-page">
        <div class="brief-masthead">
          <div>
            <div class="page-eyebrow">Briefing</div>
            <div class="brief-greeting skel" style={{ width: 320, height: 44 }} />
            <div class="brief-weather skel" style={{ width: 480, height: 22, marginTop: 12 }} />
          </div>
        </div>
        <div class="brief-grid">
          <div class="brief-card skel" style={{ height: 260 }} />
          <div class="brief-card skel" style={{ height: 200 }} />
        </div>
      </div>
    );
  }

  const totalObs = data.stats.observations_yesterday + data.stats.active_accounts_count;
  const isSparse = totalObs < 5 && data.next_actions_due.length === 0;

  return (
    <div class="v2-page">
      <div class="brief-masthead">
        <div>
          <div class="page-eyebrow">{formatBriefingDate(data.briefing_date)}</div>
          <h1 class="brief-greeting">{greetingFor(new Date())}.</h1>
          <p class="brief-weather">{data.weather_line}</p>
          {data.metadata.fallback && (
            <div
              style={{
                marginTop: 8,
                fontFamily: 'var(--f-mono)',
                fontSize: 11,
                color: 'var(--text-faint)',
                letterSpacing: '0.04em',
              }}
            >
              narrative unavailable · template fallback
            </div>
          )}
        </div>
        <div class="brief-stats-bar">
          <Stat n={data.stats.captures_yesterday} label="Captures" sub="yesterday" />
          <Stat n={data.stats.observations_yesterday} label="Observations" sub="yesterday" />
          <Stat n={data.stats.open_next_actions} label="Open" sub="actions" />
          <Stat n={data.stats.active_accounts_count} label="Active" sub="accounts" />
        </div>
      </div>

      <OnboardingChecklist me={me} />

      <div class="brief-grid pop">
        <div class="brief-card">
          <div class="brief-card-head">
            <h3>
              <Icons.Flag size={13} />
              Next actions due
              <span class="count">{data.next_actions_due.length}</span>
            </h3>
          </div>
          {data.next_actions_due.length === 0 ? (
            <div class="brief-empty">Nothing due today. You're caught up.</div>
          ) : (
            <div>
              {data.next_actions_due.map((na) => (
                <div
                  key={na.id}
                  class="stale-row"
                  onClick={() => onSelectEntity(na.entity_id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        color: 'var(--text)',
                        letterSpacing: '-0.005em',
                        marginBottom: 3,
                      }}
                    >
                      {na.text}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: 'var(--text-muted)',
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <span>{na.entity_name}</span>
                      {na.sub && (
                        <>
                          <span style={{ color: 'var(--text-faint)' }}>·</span>
                          <span>{na.sub}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span class={`v2-pill ${na.is_overdue ? 'pill-danger' : 'pill-info'}`}>
                    <span class="dot" />
                    {na.is_overdue ? 'Overdue' : 'Due'} {formatDueDate(na.due_date)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div class="brief-card">
          <div class="brief-card-head">
            <h3>
              <Icons.Clock size={13} />
              Going cold
              <span class="count">{data.going_cold.length}</span>
            </h3>
          </div>
          {data.going_cold.length === 0 ? (
            <div class="brief-empty">Your portfolio is fresh.</div>
          ) : (
            <div>
              {data.going_cold.map((g) => (
                <div
                  key={g.entity_id}
                  class="stale-row"
                  onClick={() => onSelectEntity(g.entity_id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        color: 'var(--text)',
                        letterSpacing: '-0.005em',
                        marginBottom: 3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {g.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: 'var(--text-muted)',
                        fontFamily: 'var(--f-mono)',
                      }}
                    >
                      {g.days_since_touch}d · cadence {g.cadence_days}d
                    </div>
                  </div>
                  <FreshnessPill freshness={g.freshness} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isSparse && (
        <div
          style={{
            marginTop: 20,
            padding: '14px 16px',
            background: 'var(--accent-tint-08)',
            border: '1px solid var(--accent-tint-22)',
            borderRadius: 'var(--r-lg)',
            color: 'var(--text-2)',
            fontFamily: 'var(--f-serif)',
            fontStyle: 'italic',
            fontSize: 14.5,
            lineHeight: 1.5,
          }}
        >
          Patterns and signals get richer as you add more captures — keep capturing.
        </div>
      )}

      <div
        style={{
          marginTop: 24,
          paddingTop: 12,
          borderTop: '1px solid var(--border-subtle)',
          fontFamily: 'var(--f-mono)',
          fontSize: 10.5,
          color: 'var(--text-faint)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {data.tier} tier · {cacheState === 'hit' ? 'cached' : 'fresh'} ·{' '}
        {data.metadata.fallback ? 'template' : 'haiku'}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// First-account empty-state (onboarding.md §6)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Shown on /today when the tenant has an industry template but zero account
 * entities (§6.1). Two affordances: an inline single-account form (§6.2 —
 * name required, optional domain) and a "Import a CSV instead" link.
 *
 * B.2 wrinkle: `tenants` has no `metadata` column, so the §6.4 skipped-import
 * copy variant can't be keyed off `onboarding_skipped_import`. We always use
 * the §6.4 row-1 copy ("Your portfolio is empty.").
 *
 * The CSV path here reuses the onboarding `importCsv` client fn inline rather
 * than opening a modal — matches how Settings.tsx keeps its connect form
 * inline. AI enrichment is deferred (B.2 deviation #3).
 */
function FirstAccountEmptyState({ onAdded }: { onAdded: () => void }) {
  const [mode, setMode] = useState<'choose' | 'add' | 'csv'>('choose');

  return (
    <div class="v2-page">
      <div class="brief-masthead">
        <div>
          <div class="page-eyebrow">Today</div>
          <h1 class="brief-greeting">Your portfolio is empty.</h1>
          <p class="brief-weather">Add your first account to start tracking what matters.</p>
        </div>
      </div>

      <div class="brief-card" style={{ maxWidth: 480, marginTop: 8 }}>
        {mode === 'choose' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button type="button" class="btn btn-primary" onClick={() => setMode('add')}>
              Add an account
            </button>
            <button type="button" class="btn" onClick={() => setMode('csv')}>
              Import a CSV instead
            </button>
          </div>
        )}

        {mode === 'add' && (
          <InlineAddAccountForm onAdded={onAdded} onCancel={() => setMode('choose')} />
        )}

        {mode === 'csv' && (
          <InlineCsvImport onImported={onAdded} onCancel={() => setMode('choose')} />
        )}
      </div>
    </div>
  );
}

function InlineAddAccountForm({
  onAdded,
  onCancel,
}: {
  onAdded: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await addAccountOnboarding({
        name: name.trim(),
        domain: domain.trim() || undefined,
      });
      onAdded();
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <label
        for="brief-acct-name"
        style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}
      >
        Account name
      </label>
      <input
        id="brief-acct-name"
        type="text"
        class="search-bar"
        placeholder="Acme Corp"
        value={name}
        // biome-ignore lint/a11y/noAutofocus: §6.2 calls for name autofocus on the add-account form
        autoFocus
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
        style={{ marginBottom: 12, width: '100%' }}
      />
      <label
        for="brief-acct-domain"
        style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}
      >
        Domain{' '}
        <span class="muted" style={{ fontWeight: 400 }}>
          (optional)
        </span>
      </label>
      <input
        id="brief-acct-domain"
        type="text"
        class="search-bar"
        placeholder="e.g., acmecorp.com"
        value={domain}
        onInput={(e) => setDomain((e.target as HTMLInputElement).value)}
        style={{ marginBottom: 12, width: '100%' }}
      />
      {error && (
        <p class="error-banner" style={{ marginBottom: 12 }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" class="btn btn-primary" disabled={saving || name.trim().length === 0}>
          {saving ? 'Adding…' : 'Add account'}
        </button>
        <button type="button" class="btn" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function InlineCsvImport({
  onImported,
  onCancel,
}: {
  onImported: () => void;
  onCancel: () => void;
}) {
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  async function handleFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setError(null);
    setResult(null);
    setImporting(true);
    try {
      const text = await file.text();
      const res = await importCsv(text);
      setResult({ imported: res.imported, skipped: res.skipped_duplicates });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setImporting(false);
      input.value = '';
    }
  }

  return (
    <div>
      <p class="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Upload a CSV with a name column (also "Account Name" or "Company") plus optional domain,
        industry, and location. Max 200 accounts, 1 MB.
      </p>
      {!result && (
        <label class="btn btn-primary" style={{ cursor: importing ? 'default' : 'pointer' }}>
          {importing ? 'Importing…' : 'Choose a CSV file'}
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFile}
            disabled={importing}
            style={{ display: 'none' }}
          />
        </label>
      )}
      {error && (
        <p class="error-banner" style={{ marginTop: 12 }}>
          {error}
        </p>
      )}
      {result && (
        <p style={{ marginTop: 4, fontSize: 14 }}>
          Imported <strong>{result.imported}</strong>{' '}
          {result.imported === 1 ? 'account' : 'accounts'}.
          {result.skipped > 0 && <> Skipped {result.skipped} that already existed.</>}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {result ? (
          <button type="button" class="btn btn-primary" onClick={onImported}>
            View my portfolio
          </button>
        ) : (
          <button type="button" class="btn" disabled={importing} onClick={onCancel}>
            Back
          </button>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Post-wizard checklist (onboarding.md §1.3 / §8.5)
// ───────────────────────────────────────────────────────────────────────────

const CHECKLIST_DISMISS_KEY = 'ka_onboarding_checklist_dismissed';

/**
 * Dismissible setup checklist on /today (§1.3). Five items; at MVP only two
 * are actionable — "first capture" links to /capture, "set up BYOK" links to
 * Settings and auto-checks off `tenant.byok_enabled`. Calendar (Group F),
 * push (not yet built), and invite-teammates (Phase 1.x team tier) render
 * disabled per the spec.
 *
 * Dismiss state lives in localStorage — `tenants` has no `metadata` column
 * (the B.2 wrinkle), and a per-browser "permanent" dismiss is good enough for
 * MVP. A per-user persisted dismiss + the §8.5 "collapse to a Setup link in
 * the user-menu" affordance are Phase 1.x polish. Because 3 of 5 items can't
 * be completed at MVP, the §8.5 "all items checked" auto-collapse path is
 * inert — the card goes away via explicit dismiss only.
 */
function OnboardingChecklist({ me }: { me: MeResponse | null }) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(CHECKLIST_DISMISS_KEY) === '1',
  );
  if (dismissed || !me) return null;

  const byokDone = me.tenant.byok_enabled;

  function dismiss() {
    localStorage.setItem(CHECKLIST_DISMISS_KEY, '1');
    setDismissed(true);
  }

  return (
    <div
      class="brief-card"
      style={{ marginBottom: 16, position: 'relative', borderColor: 'var(--accent-tint-22)' }}
    >
      <div class="brief-card-head">
        <h3>
          <Icons.Check size={13} />
          Finish setting up
        </h3>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss checklist"
          class="muted"
          style={{
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: 1,
            padding: '0 4px',
          }}
        >
          ×
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <ChecklistItem label="Try your first capture" href="/capture" />
        <ChecklistItem label="Connect your calendar" comingSoon />
        <ChecklistItem label="Enable push notifications" comingSoon />
        <ChecklistItem label="Set up BYOK" href="/settings/advanced" done={byokDone} />
        <ChecklistItem label="Invite teammates" note="Team plan" />
      </div>
    </div>
  );
}

function ChecklistItem({
  label,
  href,
  done,
  comingSoon,
  note,
}: {
  label: string;
  href?: string;
  done?: boolean;
  comingSoon?: boolean;
  note?: string;
}) {
  const disabled = comingSoon || (!href && !done);
  const rightNote = comingSoon ? 'Coming soon' : note;
  const inner = (
    <div
      class="stale-row"
      style={{
        cursor: href && !done ? 'pointer' : 'default',
        opacity: disabled && !done ? 0.55 : 1,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          width: 16,
          height: 16,
          marginRight: 10,
          borderRadius: 4,
          border: '1.5px solid var(--border)',
          background: done ? 'var(--accent)' : 'transparent',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {done && <Icons.Check size={11} />}
      </span>
      <span style={{ flex: 1, fontSize: 13.5, color: 'var(--text)' }}>{label}</span>
      {rightNote && (
        <span class="muted" style={{ fontSize: 11.5 }}>
          {rightNote}
        </span>
      )}
    </div>
  );
  if (href && !done) {
    return (
      <a href={href} style={{ textDecoration: 'none' }}>
        {inner}
      </a>
    );
  }
  return inner;
}

// ───────────────────────────────────────────────────────────────────────────
// Components
// ───────────────────────────────────────────────────────────────────────────

function Stat({ n, label, sub }: { n: number; label: string; sub: string }) {
  return (
    <div class="brief-stat">
      <div class="brief-stat-label">{label}</div>
      <div class="brief-stat-value">{n}</div>
      <div class="brief-stat-sub">{sub}</div>
    </div>
  );
}

function FreshnessPill({ freshness }: { freshness: 'fresh' | 'aging' | 'stale' }) {
  const cls =
    freshness === 'fresh' ? 'pill-ok' : freshness === 'aging' ? 'pill-warn' : 'pill-danger';
  return (
    <span class={`v2-pill ${cls}`}>
      <span class="dot" />
      {freshness}
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

function greetingFor(d: Date): string {
  const hour = d.getHours();
  if (hour < 5) return 'Late night';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Late night';
}

function formatBriefingDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatDueDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
